export * from "./gen/types.gen.js"

import { createClient } from "./gen/client/client.gen.js"
import { type Config } from "./gen/client/types.gen.js"
import { OpenAnalystClient } from "./gen/sdk.gen.js"
import type * as Generated from "./gen/types.gen.js"
import { wrapClientError } from "../error-interceptor.js"
export { type Config as OpenAnalystClientConfig, OpenAnalystClient }
export { OpenAnalystClient as OpencodeClient }

type CompatOptions<ThrowOnError extends boolean = boolean> = {
  throwOnError?: ThrowOnError
  signal?: AbortSignal
  headers?: RequestInit["headers"]
  parseAs?: "arrayBuffer" | "auto" | "blob" | "formData" | "json" | "stream" | "text"
}

type ApiSuccess<Data> = {
  data: Data
  error?: undefined
  request?: Request
  response?: Response
}

type ApiFailure = {
  data?: undefined
  error: Record<string, unknown>
  request?: Request
  response?: Response
}

type ApiResult<Data, ThrowOnError extends boolean = false> = ThrowOnError extends true
  ? Promise<ApiSuccess<Data>>
  : Promise<ApiSuccess<Data> | ApiFailure>

type LocationQuery = {
  directory?: string
  workspace?: string
}

type LocationRef = {
  directory: string
  workspaceID?: string
}

type LocationInput = {
  location?: LocationQuery
}

type LocationData<Data> = {
  location: LocationRef
  data: Data
}

type ProjectDirectory = {
  directory: string
  type?: "main" | "root" | "git_worktree" | string
}

type ProjectCopyInfo = {
  directory: string
  name?: string
  strategy?: string
}

type FileSearchResult = {
  path: string
  type: "file" | "directory"
}

type VcsFileStatus = {
  file: string
  additions: number
  deletions: number
  status?: "added" | "deleted" | "modified"
}

type ReferenceInfo = LocationRef & {
  name: string
  path: string
  hidden?: boolean
  source:
    | {
        type: "git"
        repository: string
        path?: string
      }
    | {
        type: string
        path: string
        repository?: string
        text?: { value?: string; start?: number; end?: number }
      }
}

type RawClient = {
  get: <Data, ThrowOnError extends boolean = false>(options: RawOptions<ThrowOnError>) => ApiResult<Data, ThrowOnError>
  post: <Data, ThrowOnError extends boolean = false>(options: RawOptions<ThrowOnError>) => ApiResult<Data, ThrowOnError>
  delete: <Data, ThrowOnError extends boolean = false>(
    options: RawOptions<ThrowOnError>,
  ) => ApiResult<Data, ThrowOnError>
}

type RawOptions<ThrowOnError extends boolean> = CompatOptions<ThrowOnError> & {
  url: string
  path?: Record<string, unknown>
  query?: Record<string, unknown>
  body?: unknown
}

export type OpenAnalystClientV2 = OpenAnalystClient & {
  v2: OpenAnalystClientV2
  location: {
    get: <ThrowOnError extends boolean = false>(
      input?: LocationInput,
      options?: CompatOptions<ThrowOnError>,
    ) => ApiResult<LocationRef, ThrowOnError>
  }
  agent: {
    list: <ThrowOnError extends boolean = false>(
      input?: LocationInput,
      options?: CompatOptions<ThrowOnError>,
    ) => ApiResult<LocationData<Array<Generated.Agent & { id: string }>>, ThrowOnError>
  }
  connector: {
    list: <ThrowOnError extends boolean = false>(
      input?: LocationInput,
      options?: CompatOptions<ThrowOnError>,
    ) => ApiResult<LocationData<Generated.Auth[]>, ThrowOnError>
  }
  model: {
    list: <ThrowOnError extends boolean = false>(
      input?: LocationInput,
      options?: CompatOptions<ThrowOnError>,
    ) => ApiResult<LocationData<Generated.Model[]>, ThrowOnError>
  }
  provider: Omit<OpenAnalystClient["provider"], "list"> & {
    list: {
      <ThrowOnError extends boolean = false>(
        input: LocationInput,
        options?: CompatOptions<ThrowOnError>,
      ): ApiResult<LocationData<Generated.Provider[]>, ThrowOnError>
      <ThrowOnError extends boolean = false>(
        input?: LocationQuery,
        options?: CompatOptions<ThrowOnError>,
      ): ApiResult<Generated.ProviderListResponse, ThrowOnError>
    }
  }
  reference: {
    list: <ThrowOnError extends boolean = false>(
      input?: LocationInput,
      options?: CompatOptions<ThrowOnError>,
    ) => ApiResult<LocationData<ReferenceInfo[]>, ThrowOnError>
  }
  skill: {
    list: <ThrowOnError extends boolean = false>(
      input?: LocationInput,
      options?: CompatOptions<ThrowOnError>,
    ) => ApiResult<LocationData<Array<Generated.AppSkillsResponse[number] & { id: string }>>, ThrowOnError>
  }
  fs: {
    find: <ThrowOnError extends boolean = false>(
      input: { query: string; limit?: number | string; location?: LocationQuery },
      options?: CompatOptions<ThrowOnError>,
    ) => ApiResult<{ data: FileSearchResult[] }, ThrowOnError>
  }
  project: OpenAnalystClient["project"] & {
    directories: <ThrowOnError extends boolean = false>(
      input: { projectID: string; workspace?: string },
      options?: CompatOptions<ThrowOnError>,
    ) => ApiResult<ProjectDirectory[], ThrowOnError>
  }
  experimental: Omit<OpenAnalystClient["experimental"], "workspace" | "session"> & {
    projectCopy: {
      create: <ThrowOnError extends boolean = false>(
        input: { projectID: string; strategy: string; directory: string; name?: string; context?: string },
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<ProjectCopyInfo, ThrowOnError>
      remove: <ThrowOnError extends boolean = false>(
        input: { projectID: string; directory: string; force: boolean; workspace?: string },
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<unknown, ThrowOnError>
      refresh: <ThrowOnError extends boolean = false>(
        input: { projectID: string; workspace?: string },
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<unknown, ThrowOnError>
    }
    workspace: Omit<OpenAnalystClient["experimental"]["workspace"], "list"> & {
      list: <ThrowOnError extends boolean = false>(
        input?: LocationQuery,
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<Array<Generated.Workspace & { timeUsed: number }>, ThrowOnError>
      syncList: <ThrowOnError extends boolean = false>(
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<unknown, ThrowOnError>
      warp: <ThrowOnError extends boolean = false>(
        input: { id: string | null; sessionID: string; copyChanges?: boolean },
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<unknown, ThrowOnError>
    }
    session: OpenAnalystClient["experimental"]["session"] & {
      background: <ThrowOnError extends boolean = false>(
        input: { sessionID: string; directory?: string; workspace?: string },
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<unknown, ThrowOnError>
    }
    controlPlane: {
      moveSession: <ThrowOnError extends boolean = false>(
        input: { sessionID: string; destination: { directory: string }; moveChanges: boolean },
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<unknown, ThrowOnError>
    }
  }
  vcs: Omit<OpenAnalystClient["vcs"], "diff"> & {
    status: <ThrowOnError extends boolean = false>(
      input?: LocationQuery,
      options?: CompatOptions<ThrowOnError>,
    ) => ApiResult<VcsFileStatus[], ThrowOnError>
    diff: <ThrowOnError extends boolean = false>(
      input: LocationQuery & { mode: "git" | "branch"; context?: number },
      options?: CompatOptions<ThrowOnError>,
    ) => ReturnType<OpenAnalystClient["vcs"]["diff"]>
  }
  session: OpenAnalystClient["session"] & {
    permission: {
      list: <ThrowOnError extends boolean = false>(
        input: { sessionID: string; directory?: string; workspace?: string },
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<{ data: Generated.PermissionRequest[] }, ThrowOnError>
    }
    question: {
      list: <ThrowOnError extends boolean = false>(
        input: { sessionID: string; directory?: string; workspace?: string },
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<{ data: Generated.QuestionRequest[] }, ThrowOnError>
    }
  }
  permission: OpenAnalystClient["permission"] & {
    saved: {
      list: <ThrowOnError extends boolean = false>(
        input: { projectID: string },
        options?: CompatOptions<ThrowOnError>,
      ) => ApiResult<{ data: Generated.PermissionRequest[] }, ThrowOnError>
    }
  }
}

function makeLocation(input: LocationQuery | undefined, fallback?: string): LocationRef {
  return {
    directory: input?.directory ?? fallback ?? "",
    workspaceID: input?.workspace,
  }
}

function locationInput(input?: LocationInput | LocationQuery): LocationQuery | undefined {
  if (!input) return undefined
  if (Object.hasOwn(input, "location")) return (input as LocationInput).location
  return input as LocationQuery
}

function locationData<Data, ThrowOnError extends boolean>(
  input: LocationQuery | undefined,
  fallback: string | undefined,
  request: ApiResult<Data, ThrowOnError>,
): ApiResult<LocationData<Data>, ThrowOnError> {
  return request.then((response) => {
    if (response.data === undefined) return response as Awaited<ApiResult<LocationData<Data>, ThrowOnError>>
    return {
      ...response,
      data: {
        location: makeLocation(input, fallback),
        data: response.data,
      },
    } as Awaited<ApiResult<LocationData<Data>, ThrowOnError>>
  }) as ApiResult<LocationData<Data>, ThrowOnError>
}

function success<Data, ThrowOnError extends boolean>(data: Data): ApiResult<Data, ThrowOnError> {
  return Promise.resolve({ data }) as ApiResult<Data, ThrowOnError>
}

function pick(value: string | null, fallback?: string, encode?: (value: string) => string) {
  if (!value) return
  if (!fallback) return value
  if (value === fallback) return fallback
  if (encode && value === encode(fallback)) return fallback
  return value
}

function rewrite(request: Request, values: { directory?: string; workspace?: string }) {
  if (request.method !== "GET" && request.method !== "HEAD") return request

  const url = new URL(request.url)
  let changed = false

  for (const [name, key] of [
    ["x-openanalyst-directory", "directory"],
    ["x-openanalyst-workspace", "workspace"],
  ] as const) {
    const value = pick(
      request.headers.get(name),
      key === "directory" ? values.directory : values.workspace,
      key === "directory" ? encodeURIComponent : undefined,
    )
    if (!value) continue
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
    changed = true
  }

  if (!changed) return request

  const next = new Request(url, request)
  next.headers.delete("x-openanalyst-directory")
  next.headers.delete("x-openanalyst-workspace")
  return next
}

export function createOpenAnalystClient(
  config?: Config & { directory?: string; experimental_workspaceID?: string },
): OpenAnalystClientV2 {
  if (!config?.fetch) {
    const customFetch: any = (req: any) => {
      // @ts-ignore
      req.timeout = false
      return fetch(req)
    }
    config = {
      ...config,
      fetch: customFetch,
    }
  }

  if (config?.directory) {
    config.headers = {
      ...config.headers,
      "x-openanalyst-directory": encodeURIComponent(config.directory),
    }
  }

  if (config?.experimental_workspaceID) {
    config.headers = {
      ...config.headers,
      "x-openanalyst-workspace": config.experimental_workspaceID,
    }
  }

  const client = createClient(config)
  client.interceptors.request.use((request) =>
    rewrite(request, {
      directory: config?.directory,
      workspace: config?.experimental_workspaceID,
    }),
  )
  client.interceptors.response.use((response) => {
    const contentType = response.headers.get("content-type")
    if (contentType === "text/html")
      throw new Error(
        "Request is not supported by this version of OpenAnalyst Server (Server responded with text/html)",
      )

    return response
  })
  client.interceptors.error.use(wrapClientError)
  const openAnalystClient = new OpenAnalystClient({ client })
  const raw = client as unknown as RawClient
  const compat = openAnalystClient as OpenAnalystClientV2
  const project = openAnalystClient.project as OpenAnalystClientV2["project"]
  const experimental = openAnalystClient.experimental as OpenAnalystClientV2["experimental"]
  const workspace = openAnalystClient.experimental.workspace as OpenAnalystClientV2["experimental"]["workspace"]
  const vcs = openAnalystClient.vcs as OpenAnalystClientV2["vcs"]
  const session = openAnalystClient.session as OpenAnalystClientV2["session"]
  const permission = openAnalystClient.permission as OpenAnalystClientV2["permission"]
  const provider = openAnalystClient.provider as OpenAnalystClientV2["provider"]
  const experimentalSession = openAnalystClient.experimental.session as OpenAnalystClientV2["experimental"]["session"]

  Object.assign(project, {
    directories<ThrowOnError extends boolean = false>(
      input: { projectID: string; workspace?: string },
      options?: CompatOptions<ThrowOnError>,
    ) {
      return raw.get<ProjectDirectory[], ThrowOnError>({
        ...options,
        url: "/project/{projectID}/directories",
        path: { projectID: input.projectID },
        query: { workspace: input.workspace },
      })
    },
  })

  Object.assign(experimental, {
    projectCopy: {
      create<ThrowOnError extends boolean = false>(
        input: { projectID: string; strategy: string; directory: string; name?: string; context?: string },
        options?: CompatOptions<ThrowOnError>,
      ) {
        return raw.post<ProjectCopyInfo, ThrowOnError>({
          ...options,
          url: "/experimental/project/{projectID}/copy",
          path: { projectID: input.projectID },
          body: {
            strategy: input.strategy,
            directory: input.directory,
            name: input.name,
            context: input.context,
          },
          headers: { "Content-Type": "application/json", ...options?.headers },
        })
      },
      remove<ThrowOnError extends boolean = false>(
        input: { projectID: string; directory: string; force: boolean; workspace?: string },
        options?: CompatOptions<ThrowOnError>,
      ) {
        return raw.delete<unknown, ThrowOnError>({
          ...options,
          url: "/experimental/project/{projectID}/copy",
          path: { projectID: input.projectID },
          query: { workspace: input.workspace },
          body: { directory: input.directory, force: input.force },
          headers: { "Content-Type": "application/json", ...options?.headers },
        })
      },
      refresh<ThrowOnError extends boolean = false>(
        input: { projectID: string; workspace?: string },
        options?: CompatOptions<ThrowOnError>,
      ) {
        return raw.post<unknown, ThrowOnError>({
          ...options,
          url: "/experimental/project/{projectID}/copy/refresh",
          path: { projectID: input.projectID },
          query: { workspace: input.workspace },
        })
      },
    },
    controlPlane: {
      moveSession<ThrowOnError extends boolean = false>(
        input: { sessionID: string; destination: { directory: string }; moveChanges: boolean },
        options?: CompatOptions<ThrowOnError>,
      ) {
        return raw.post<unknown, ThrowOnError>({
          ...options,
          url: "/experimental/control-plane/move-session",
          body: input,
          headers: { "Content-Type": "application/json", ...options?.headers },
        })
      },
    },
  })

  Object.assign(workspace, {
    list<ThrowOnError extends boolean = false>(input?: LocationQuery, options?: CompatOptions<ThrowOnError>) {
      return raw
        .get<Generated.Workspace[], ThrowOnError>({
          ...options,
          url: "/experimental/workspace",
          query: input,
        })
        .then((response) => {
          if (response.data === undefined)
            return response as Awaited<ApiResult<Array<Generated.Workspace & { timeUsed: number }>, ThrowOnError>>
          return {
            ...response,
            data: response.data.map((item) => ({ ...item, timeUsed: 0 })),
          } as Awaited<ApiResult<Array<Generated.Workspace & { timeUsed: number }>, ThrowOnError>>
        }) as ApiResult<Array<Generated.Workspace & { timeUsed: number }>, ThrowOnError>
    },
    syncList<ThrowOnError extends boolean = false>(options?: CompatOptions<ThrowOnError>) {
      return openAnalystClient.sync.start(undefined, options)
    },
    warp<ThrowOnError extends boolean = false>(
      input: { id: string | null; sessionID: string; copyChanges?: boolean },
      options?: CompatOptions<ThrowOnError>,
    ) {
      if (!input.id) return success<unknown, ThrowOnError>(true)
      return workspace.sessionRestore({ id: input.id, sessionID: input.sessionID }, options)
    },
  })

  Object.assign(experimentalSession, {
    background<ThrowOnError extends boolean = false>(
      input: { sessionID: string; directory?: string; workspace?: string },
      options?: CompatOptions<ThrowOnError>,
    ) {
      return raw.post<unknown, ThrowOnError>({
        ...options,
        url: "/experimental/session/{sessionID}/background",
        path: { sessionID: input.sessionID },
        query: { directory: input.directory, workspace: input.workspace },
      })
    },
  })

  const originalDiff = openAnalystClient.vcs.diff.bind(openAnalystClient.vcs)
  Object.assign(vcs, {
    status<ThrowOnError extends boolean = false>(input?: LocationQuery, options?: CompatOptions<ThrowOnError>) {
      return raw
        .get<Generated.File[], ThrowOnError>({
          ...options,
          url: "/file/status",
          query: input,
        })
        .then((response) => {
          if (response.data === undefined) return response as Awaited<ApiResult<VcsFileStatus[], ThrowOnError>>
          return {
            ...response,
            data: response.data.map((file) => ({
              file: file.path,
              additions: file.added,
              deletions: file.removed,
              status: file.status,
            })),
          } as Awaited<ApiResult<VcsFileStatus[], ThrowOnError>>
        }) as ApiResult<VcsFileStatus[], ThrowOnError>
    },
    diff<ThrowOnError extends boolean = false>(
      input: LocationQuery & { mode: "git" | "branch"; context?: number },
      options?: CompatOptions<ThrowOnError>,
    ) {
      return originalDiff(
        { directory: input.directory, workspace: input.workspace, mode: input.mode },
        options,
      ) as ReturnType<OpenAnalystClient["vcs"]["diff"]>
    },
  })

  Object.assign(session, {
    permission: {
      list<ThrowOnError extends boolean = false>(
        input: { sessionID: string; directory?: string; workspace?: string },
        options?: CompatOptions<ThrowOnError>,
      ) {
        return raw
          .get<Generated.PermissionRequest[], ThrowOnError>({
            ...options,
            url: "/permission",
            query: { directory: input.directory, workspace: input.workspace },
          })
          .then((response) => {
            if (response.data === undefined)
              return response as Awaited<ApiResult<{ data: Generated.PermissionRequest[] }, ThrowOnError>>
            return {
              ...response,
              data: { data: response.data.filter((item) => item.sessionID === input.sessionID) },
            } as Awaited<ApiResult<{ data: Generated.PermissionRequest[] }, ThrowOnError>>
          }) as ApiResult<{ data: Generated.PermissionRequest[] }, ThrowOnError>
      },
    },
    question: {
      list<ThrowOnError extends boolean = false>(
        input: { sessionID: string; directory?: string; workspace?: string },
        options?: CompatOptions<ThrowOnError>,
      ) {
        return raw
          .get<Generated.QuestionRequest[], ThrowOnError>({
            ...options,
            url: "/question",
            query: { directory: input.directory, workspace: input.workspace },
          })
          .then((response) => {
            if (response.data === undefined)
              return response as Awaited<ApiResult<{ data: Generated.QuestionRequest[] }, ThrowOnError>>
            return {
              ...response,
              data: { data: response.data.filter((item) => item.sessionID === input.sessionID) },
            } as Awaited<ApiResult<{ data: Generated.QuestionRequest[] }, ThrowOnError>>
          }) as ApiResult<{ data: Generated.QuestionRequest[] }, ThrowOnError>
      },
    },
  })

  Object.assign(permission, {
    saved: {
      list<ThrowOnError extends boolean = false>(
        _input: { projectID: string },
        _options?: CompatOptions<ThrowOnError>,
      ) {
        return success<{ data: Generated.PermissionRequest[] }, ThrowOnError>({ data: [] })
      },
    },
  })

  Object.assign(provider, {
    list<ThrowOnError extends boolean = false>(
      input?: LocationInput | LocationQuery,
      options?: CompatOptions<ThrowOnError>,
    ) {
      const query = locationInput(input)
      const request = raw
        .get<Generated.ProviderListResponse, ThrowOnError>({
          ...options,
          url: "/provider",
          query,
        })
        .then((response) => {
          if (!input || !("location" in input) || response.data === undefined)
            return response as Awaited<
              ApiResult<LocationData<Generated.Provider[]> | Generated.ProviderListResponse, ThrowOnError>
            >
          return {
            ...response,
            data: {
              location: makeLocation(query, config?.directory),
              data: response.data.all,
            },
          } as Awaited<ApiResult<LocationData<Generated.Provider[]> | Generated.ProviderListResponse, ThrowOnError>>
        })
      return request as ApiResult<LocationData<Generated.Provider[]> | Generated.ProviderListResponse, ThrowOnError>
    },
  })

  return Object.assign(compat, {
    v2: compat,
    location: {
      async get<ThrowOnError extends boolean = false>(input?: LocationInput, options?: CompatOptions<ThrowOnError>) {
        const query = locationInput(input)
        const response = await raw.get<Generated.Path, ThrowOnError>({
          ...options,
          url: "/path",
          query,
        })
        if (response.data === undefined) return response as Awaited<ApiResult<LocationRef, ThrowOnError>>
        return {
          ...response,
          data: {
            directory: response.data.directory,
            workspaceID: query?.workspace,
          },
        } as Awaited<ApiResult<LocationRef, ThrowOnError>>
      },
    },
    agent: {
      list<ThrowOnError extends boolean = false>(input?: LocationInput, options?: CompatOptions<ThrowOnError>) {
        const query = locationInput(input)
        return locationData(
          query,
          config?.directory,
          raw
            .get<Generated.Agent[], ThrowOnError>({
              ...options,
              url: "/agent",
              query,
            })
            .then((response) => {
              if (response.data === undefined)
                return response as Awaited<ApiResult<Array<Generated.Agent & { id: string }>, ThrowOnError>>
              return {
                ...response,
                data: response.data.map((agent) => ({ ...agent, id: agent.name })),
              } as Awaited<ApiResult<Array<Generated.Agent & { id: string }>, ThrowOnError>>
            }) as ApiResult<Array<Generated.Agent & { id: string }>, ThrowOnError>,
        )
      },
    },
    connector: {
      list<ThrowOnError extends boolean = false>(input?: LocationInput, _options?: CompatOptions<ThrowOnError>) {
        return locationData(locationInput(input), config?.directory, success<Generated.Auth[], ThrowOnError>([]))
      },
    },
    model: {
      list<ThrowOnError extends boolean = false>(input?: LocationInput, options?: CompatOptions<ThrowOnError>) {
        const query = locationInput(input)
        return locationData(
          query,
          config?.directory,
          raw
            .get<Generated.ProviderListResponse, ThrowOnError>({
              ...options,
              url: "/provider",
              query,
            })
            .then((response) => {
              if (response.data === undefined) return response as Awaited<ApiResult<Generated.Model[], ThrowOnError>>
              return {
                ...response,
                data: response.data.all.flatMap((provider) => Object.values(provider.models)),
              } as Awaited<ApiResult<Generated.Model[], ThrowOnError>>
            }) as ApiResult<Generated.Model[], ThrowOnError>,
        )
      },
    },
    reference: {
      list<ThrowOnError extends boolean = false>(input?: LocationInput, _options?: CompatOptions<ThrowOnError>) {
        return locationData(locationInput(input), config?.directory, success<ReferenceInfo[], ThrowOnError>([]))
      },
    },
    skill: {
      list<ThrowOnError extends boolean = false>(input?: LocationInput, options?: CompatOptions<ThrowOnError>) {
        const query = locationInput(input)
        return locationData(
          query,
          config?.directory,
          raw
            .get<Generated.AppSkillsResponse, ThrowOnError>({
              ...options,
              url: "/skill",
              query,
            })
            .then((response) => {
              if (response.data === undefined)
                return response as Awaited<
                  ApiResult<Array<Generated.AppSkillsResponse[number] & { id: string }>, ThrowOnError>
                >
              return {
                ...response,
                data: response.data.map((skill) => ({ ...skill, id: skill.name })),
              } as Awaited<ApiResult<Array<Generated.AppSkillsResponse[number] & { id: string }>, ThrowOnError>>
            }) as ApiResult<Array<Generated.AppSkillsResponse[number] & { id: string }>, ThrowOnError>,
        )
      },
    },
    fs: {
      find<ThrowOnError extends boolean = false>(
        input: { query: string; limit?: number | string; location?: LocationQuery },
        options?: CompatOptions<ThrowOnError>,
      ) {
        return raw
          .get<string[], ThrowOnError>({
            ...options,
            url: "/find/file",
            query: {
              ...input.location,
              query: input.query,
              limit: Number(input.limit ?? 20),
            },
          })
          .then((response) => {
            if (response.data === undefined)
              return response as Awaited<ApiResult<{ data: FileSearchResult[] }, ThrowOnError>>
            return {
              ...response,
              data: {
                data: response.data.map((item) => ({ path: item, type: "file" as const })),
              },
            } as Awaited<ApiResult<{ data: FileSearchResult[] }, ThrowOnError>>
          }) as ApiResult<{ data: FileSearchResult[] }, ThrowOnError>
      },
    },
  })
}

export const createOpencodeClient = createOpenAnalystClient
